import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, BookOpen, Bell, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { NewsletterForm } from "@/components/NewsletterForm";
import { Logo } from "@/components/Logo";

const PERKS = [
  [Gift, "Offers first", "Hear about course discounts and bundles before anyone else."],
  [BookOpen, "New lessons & articles", "Know when new courses, games and theory articles go live."],
  [Bell, "Updates from Jason", "Live sessions, events and news from The JT Hub."],
];

const TokenResult = ({ kind, token }) => {
  const [res, setRes] = useState(null);
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    api.post(`/newsletter/${kind}`, { token }).then(() => setRes({ ok: true }))
      .catch((e) => setRes({ ok: false, msg: e.response?.data?.detail || "Something went wrong. Please try again." }));
  }, [kind, token]);
  if (!res) return <p className="text-sm text-muted-foreground" data-testid="newsletter-token-loading">One moment…</p>;
  const Icon = res.ok ? CheckCircle2 : XCircle;
  const text = !res.ok ? res.msg : kind === "confirm" ? "You're on the list! I'll be in touch with offers and updates soon." : "You've been unsubscribed. You won't get any more of these emails.";
  return (
    <div className={`flex items-start gap-3 rounded-2xl border p-5 ${res.ok ? "border-primary/40 bg-primary/10" : "border-destructive/40 bg-destructive/10"}`} data-testid={`newsletter-${kind}-result`}>
      <Icon className={res.ok ? "text-primary" : "text-destructive"} size={22} />
      <div><p className="font-semibold">{text}</p><Link to="/courses" className="mt-2 inline-block text-sm text-primary underline" data-testid="newsletter-result-courses">Browse the courses</Link></div>
    </div>
  );
};

export default function KeepInTouch() {
  const [params] = useSearchParams();
  const confirm = params.get("confirm");
  const unsub = params.get("unsubscribe");
  const from = ["checkout", "register"].includes(params.get("from")) ? params.get("from") : "page";
  const fromPage = params.get("page") || "/keep-in-touch";
  return (
    <div className="mx-auto max-w-6xl px-6 py-12 sm:px-8" data-testid="keep-in-touch-page">
      <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-start">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          <Logo height={84} className="mb-8" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Let's keep in touch</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl">Stay in the loop with The JT Hub</h1>
          <p className="mt-5 max-w-lg text-base text-muted-foreground">Join the mailing list and I'll let you know about offers, new lessons and what's coming up. Tell me your instrument and I'll keep it relevant.</p>
          <ul className="mt-8 space-y-5">
            {PERKS.map(([Icon, t, d], i) => (
              <li key={t} className="flex gap-4" data-testid={`keep-in-touch-perk-${i}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Icon size={20} /></span>
                <span><b className="block">{t}</b><span className="text-sm text-muted-foreground">{d}</span></span>
              </li>
            ))}
          </ul>
        </div>
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
          <div className="relative aspect-[4/3]">
            <img src="/media/jt-two-keyboards.webp" alt="Jason Thompson playing two keyboards" className="h-full w-full object-cover" data-testid="keep-in-touch-photo" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-6 pb-4 pt-10 text-white">
              <p className="font-serif text-xl">Jason Thompson</p>
              <p className="text-xs opacity-90">Founder of The JT Hub</p>
            </div>
          </div>
          <div className="p-6 sm:p-8">
          {confirm ? <TokenResult kind="confirm" token={confirm} /> : unsub ? <TokenResult kind="unsubscribe" token={unsub} /> : <>
            <h2 className="mb-5 font-serif text-2xl">Join the mailing list</h2>
            <NewsletterForm id="page" source={from} page={fromPage} />
          </>}
          </div>
        </div>
      </div>
    </div>
  );
}
